import { useState, useEffect, useRef } from 'react';
import { MapPin, Plane } from 'lucide-react';

const CREW_A = {
  name: 'You',
  color: 'accent' as const,
  flights: [
    { day: 1, label: 'DXB→BKK', span: 2 },
    { day: 3, label: 'BKK', span: 2, layover: true },
    { day: 5, label: 'BKK→DXB', span: 2 },
  ],
};

const CREW_B = {
  name: 'Sarah',
  color: 'teal' as const,
  flights: [
    { day: 2, label: 'DXB→BKK', span: 2 },
    { day: 4, label: 'BKK', span: 2, layover: true },
    { day: 6, label: 'BKK→DXB', span: 2 },
  ],
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const OVERLAP = { startDay: 3, endDay: 5, city: 'Bangkok' };

type Phase = 'empty' | 'crew-a' | 'crew-b' | 'overlap' | 'done';

interface Props {
  active: boolean;
}

export function CrewSlide({ active }: Props) {
  const [phase, setPhase] = useState<Phase>('empty');
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (!active) return;

    setPhase('empty');
    timers.current.forEach(clearTimeout);
    timers.current = [];

    const t = (fn: () => void, ms: number) => {
      timers.current.push(setTimeout(fn, ms));
    };
    t(() => setPhase('crew-a'), 400);
    t(() => setPhase('crew-b'), 1600);
    t(() => setPhase('overlap'), 2800);
    t(() => setPhase('done'), 3600);

    return () => timers.current.forEach(clearTimeout);
  }, [active]);

  const showA = phase !== 'empty';
  const showB = phase === 'crew-b' || phase === 'overlap' || phase === 'done';
  const showOverlap = phase === 'overlap' || phase === 'done';

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8">
      <GanttAnimations />

      <div
        className="w-full max-w-[340px] mb-8 relative"
        style={{
          animation: active ? 'ob-fade-in 0.6s ease-out both' : undefined,
        }}
      >
        {/* Week header */}
        <div className="grid grid-cols-7 gap-0.5 mb-4 px-1">
          {DAYS.map((d) => (
            <span
              key={d}
              className="text-[10px] text-slate-500 text-center font-semibold tracking-wide"
            >
              {d}
            </span>
          ))}
        </div>

        {/* Crew A row */}
        <CrewRow crew={CREW_A} visible={showA} overlapDays={showOverlap ? OVERLAP : null} />

        {/* Overlap bridge */}
        <OverlapBridge visible={showOverlap} />

        {/* Crew B row */}
        <CrewRow crew={CREW_B} visible={showB} overlapDays={showOverlap ? OVERLAP : null} />

        {/* Overlap badge */}
        <div
          className="mt-5 flex items-center justify-center gap-2.5"
          style={{
            opacity: showOverlap ? 1 : 0,
            transform: showOverlap
              ? 'translateY(0) scale(1)'
              : 'translateY(12px) scale(0.8)',
            transition:
              'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          <div className="flex -space-x-2">
            <div className="w-8 h-8 rounded-full bg-accent/20 border-2 border-accent/40 flex items-center justify-center backdrop-blur-sm">
              <span className="text-[9px] font-bold text-accent">You</span>
            </div>
            <div className="w-8 h-8 rounded-full bg-teal/20 border-2 border-teal/40 flex items-center justify-center backdrop-blur-sm">
              <span className="text-[9px] font-bold text-teal">S</span>
            </div>
          </div>
          <div
            className="relative px-4 py-2 rounded-2xl bg-coral/10 border border-coral/25 overflow-hidden"
            style={{
              animation: showOverlap
                ? 'gantt-badge-glow 2s ease-in-out infinite'
                : undefined,
            }}
          >
            {showOverlap && (
              <div
                className="absolute inset-0 bg-coral/20"
                style={{ animation: 'ob-flash 0.6s ease-out forwards' }}
              />
            )}
            <span className="text-[12px] font-bold text-coral relative z-10">
              2 nights in Bangkok!
            </span>
          </div>
        </div>
      </div>

      {/* Text */}
      <h1
        className="text-[28px] leading-tight font-extrabold text-slate-100 mb-3 text-center"
        style={{
          animation: active
            ? 'ob-slide-up 0.6s ease-out 0.3s both'
            : undefined,
        }}
      >
        Find your crew
      </h1>
      <p
        className="text-[15px] text-slate-400 text-center max-w-[280px] leading-relaxed"
        style={{
          animation: active
            ? 'ob-slide-up 0.6s ease-out 0.5s both'
            : undefined,
        }}
      >
        Automatic overlap detection shows when you're in the same city as
        friends.
      </p>
    </div>
  );
}

// ── Overlap bridge between rows ──

function OverlapBridge({ visible }: { visible: boolean }) {
  const startPct = ((OVERLAP.startDay - 1) / 7) * 100;
  const widthPct = ((OVERLAP.endDay - OVERLAP.startDay + 1) / 7) * 100;

  return (
    <div className="relative h-3 my-0.5">
      <div
        className="absolute top-0 h-full rounded-sm overflow-hidden"
        style={{
          left: `${startPct}%`,
          width: `${widthPct}%`,
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.4s ease-out',
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(251,113,133,0.25) 0%, rgba(251,113,133,0.05) 50%, rgba(251,113,133,0.25) 100%)',
            animation: visible
              ? 'gantt-shimmer 1.5s ease-in-out infinite'
              : undefined,
          }}
        />
        <div className="absolute left-1/2 top-0 bottom-0 w-px border-l border-dashed border-coral/30" />
      </div>
    </div>
  );
}

// ── Crew row with plane trail + staggered blocks ──

const COLOR_MAP = {
  accent: {
    bg: 'from-accent/20 to-accent/10',
    border: 'border-accent/25',
    text: 'text-accent',
    glow: 'rgba(140,106,46,0.15)',
    plane: 'text-accent',
  },
  teal: {
    bg: 'from-teal/20 to-teal/10',
    border: 'border-teal/25',
    text: 'text-teal',
    glow: 'rgba(45,212,191,0.15)',
    plane: 'text-teal',
  },
};

interface CrewData {
  name: string;
  color: 'accent' | 'teal';
  flights: { day: number; label: string; span: number; layover?: boolean }[];
}

interface CrewRowProps {
  crew: CrewData;
  visible: boolean;
  overlapDays: typeof OVERLAP | null;
}

function CrewRow({ crew, visible, overlapDays }: CrewRowProps) {
  const colors = COLOR_MAP[crew.color] || COLOR_MAP.accent;
  const trailColor =
    crew.color === 'accent'
      ? 'rgba(140,106,46,0.3)'
      : 'rgba(45,212,191,0.3)';

  return (
    <div className="mb-3">
      {/* Name label */}
      <div className="flex items-center gap-2 mb-1.5 px-1">
        <div
          className={`w-5 h-5 rounded-full bg-gradient-to-br ${colors.bg} border ${colors.border} flex items-center justify-center backdrop-blur-sm`}
        >
          <span className={`text-[7px] font-bold ${colors.text}`}>
            {crew.name[0]}
          </span>
        </div>
        <span
          className={`text-[11px] font-semibold ${colors.text} tracking-wide`}
        >
          {crew.name}
        </span>
      </div>

      {/* Day grid */}
      <div className="relative grid grid-cols-7 gap-0.5 h-11">
        {/* Empty day cells */}
        {Array.from({ length: 7 }, (_, i) => (
          <div
            key={i}
            className="rounded-md bg-slate-800/20 border border-slate-700/15"
          />
        ))}

        {/* Flight blocks — staggered reveal */}
        {crew.flights.map((f, fi) => {
          const isOverlap =
            overlapDays &&
            f.day >= overlapDays.startDay &&
            f.day <= overlapDays.endDay;
          const blockDelay = ((f.day - 1) / 7) * 0.7;

          return (
            <div
              key={fi}
              className={`absolute top-0 h-full rounded-lg flex items-center justify-center backdrop-blur-sm ${
                f.layover
                  ? isOverlap
                    ? 'border border-dashed border-coral/40'
                    : `border border-dashed ${colors.border}`
                  : isOverlap
                    ? 'border-2 border-coral/40'
                    : `border ${colors.border}`
              }`}
              style={{
                left: `${((f.day - 1) / 7) * 100}%`,
                width: `${(f.span / 7) * 100}%`,
                padding: '0 3px',
                background: f.layover
                  ? isOverlap
                    ? 'linear-gradient(135deg, rgba(251,113,133,0.1), rgba(251,113,133,0.04))'
                    : `linear-gradient(135deg, ${colors.glow.replace('0.15', '0.08')}, transparent)`
                  : isOverlap
                    ? 'linear-gradient(135deg, rgba(251,113,133,0.2), rgba(251,113,133,0.08))'
                    : `linear-gradient(135deg, ${colors.glow}, transparent)`,
                boxShadow: isOverlap
                  ? '0 2px 12px rgba(251,113,133,0.15), inset 0 1px 0 rgba(255,255,255,0.05)'
                  : f.layover
                    ? 'none'
                    : `0 2px 8px ${colors.glow}, inset 0 1px 0 rgba(255,255,255,0.05)`,
                opacity: visible ? 1 : 0,
                transform: visible ? 'scale(1)' : 'scale(0.85)',
                transition: visible
                  ? `opacity 0.35s ease-out ${blockDelay}s, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) ${blockDelay}s, background 0.3s, border-color 0.3s, box-shadow 0.3s`
                  : 'opacity 0.2s ease-out, transform 0.2s ease-out',
              }}
            >
              {f.layover ? (
                <div className="flex items-center gap-1">
                  <MapPin
                    size={8}
                    className={isOverlap ? 'text-coral/60' : 'text-slate-500'}
                  />
                  <span
                    className={`text-[10px] font-semibold truncate ${isOverlap ? 'text-coral' : 'text-slate-400'}`}
                  >
                    {f.label}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-0.5">
                  <Plane
                    size={7}
                    className={
                      isOverlap
                        ? 'text-coral/60'
                        : `${colors.text} opacity-50`
                    }
                  />
                  <span
                    className={`text-[10px] font-semibold truncate ${isOverlap ? 'text-coral' : colors.text}`}
                  >
                    {f.label}
                  </span>
                </div>
              )}
            </div>
          );
        })}

        {/* Plane trail line */}
        <div
          className="absolute top-1/2 h-px -translate-y-1/2 pointer-events-none left-0 right-0"
          style={{
            background: `linear-gradient(to right, transparent, ${trailColor}, transparent)`,
            opacity: visible ? 1 : 0,
            animation: visible
              ? 'gantt-trail 0.9s ease-out forwards'
              : undefined,
          }}
        />

        {/* Flying plane */}
        <div
          className="absolute top-1/2 -translate-y-1/2 z-10 pointer-events-none"
          style={{
            opacity: 0,
            animation: visible
              ? 'gantt-plane-fly 0.9s ease-in-out forwards'
              : undefined,
          }}
        >
          <Plane
            size={10}
            className={`${colors.plane} drop-shadow-sm`}
            style={{ transform: 'rotate(-10deg)' }}
          />
        </div>
      </div>
    </div>
  );
}

// ── Keyframe animations ──

function GanttAnimations() {
  return (
    <style>{`
      @keyframes gantt-plane-fly {
        0% { left: -3%; opacity: 0; }
        8% { opacity: 0.8; }
        75% { opacity: 0.6; }
        100% { left: 103%; opacity: 0; }
      }
      @keyframes gantt-trail {
        0% { clip-path: inset(0 100% 0 0); opacity: 0.5; }
        60% { opacity: 0.4; }
        100% { clip-path: inset(0 0 0 0); opacity: 0; }
      }
      @keyframes gantt-shimmer {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 1; }
      }
      @keyframes gantt-badge-glow {
        0%, 100% { box-shadow: 0 0 15px rgba(251,113,133,0.1), 0 0 30px rgba(251,113,133,0.05); }
        50% { box-shadow: 0 0 25px rgba(251,113,133,0.2), 0 0 50px rgba(251,113,133,0.1); }
      }
    `}</style>
  );
}
